/**
 * PilotageActionsPanel — actions de réunion (Lot 4).
 * Les décisions prises en réunion deviennent des tâches assignées à un agent
 * (avec échéance), suivies jusqu'à leur clôture.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle, ClipboardCheck, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function fdate(iso) {
    return iso ? new Date(iso).toLocaleDateString('fr-FR') : '';
}

export default function PilotageActionsPanel({ refreshKey = 0 }) {
    const [filter, setFilter] = useState('open');
    const [data, setData] = useState(null);
    const [agents, setAgents] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [title, setTitle] = useState('');
    const [agentId, setAgentId] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/pilotage/actions?status=${filter}`);
            setData(r.data);
        } catch (e) { /* silencieux */ }
    }, [filter]);

    useEffect(() => { load(); }, [load, refreshKey]);

    useEffect(() => {
        api.get('/pilotage/agents')
            .then((r) => setAgents(r.data.agents || []))
            .catch(() => {});
    }, []);

    const create = async () => {
        if (!title.trim() || !agentId) {
            toast.error('Titre et agent assigné requis');
            return;
        }
        setBusy(true);
        try {
            await api.post('/pilotage/actions', {
                title: title.trim(), agent_id: agentId,
                due_date: dueDate ? `${dueDate}T12:00:00+00:00` : null,
            });
            toast.success('Action créée et agent notifié');
            setTitle(''); setAgentId(''); setDueDate(''); setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Création impossible");
        } finally { setBusy(false); }
    };

    const toggle = async (action) => {
        try {
            await api.patch(`/pilotage/actions/${action.id}`, {
                status: action.status === 'open' ? 'done' : 'open',
            });
            load();
        } catch (e) { toast.error('Mise à jour impossible'); }
    };

    const isOverdue = (a) => a.status === 'open' && a.due_date &&
        new Date(a.due_date) < new Date();

    return (
        <section data-testid="pilotage-actions-panel">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2"
                    style={{ color: 'var(--muted-foreground)' }}>
                    <ClipboardCheck className="w-4 h-4" style={{ color: '#681793' }} />
                    Actions de réunion
                    {data?.open_count > 0 && (
                        <span data-testid="actions-open-count"
                              className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white"
                              style={{ background: '#681793' }}>
                            {data.open_count} à faire
                        </span>
                    )}
                </h2>
                <div className="flex items-center gap-2">
                    <div className="flex rounded-lg border overflow-hidden"
                         style={{ borderColor: 'var(--border)' }}>
                        {[{ k: 'open', l: 'À faire' }, { k: 'done', l: 'Terminées' }].map((t) => (
                            <button key={t.k} type="button"
                                    data-testid={`actions-filter-${t.k}`}
                                    onClick={() => setFilter(t.k)}
                                    className="px-2.5 py-1 text-xs font-medium"
                                    style={filter === t.k
                                        ? { background: '#681793', color: '#fff' }
                                        : { color: 'var(--muted-foreground)' }}>
                                {t.l}
                            </button>
                        ))}
                    </div>
                    <button type="button" onClick={() => setShowForm((s) => !s)}
                            data-testid="action-create-btn"
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border"
                            style={{ borderColor: '#681793', color: '#681793' }}>
                        <Plus className="w-3.5 h-3.5" /> Nouvelle action
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="rounded-xl border p-3 mb-3 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]"
                     style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                     data-testid="action-create-form">
                    <input type="text" value={title} maxLength={300}
                           onChange={(e) => setTitle(e.target.value)}
                           placeholder="Décision / action à mener…"
                           data-testid="action-title-input"
                           className="px-3 py-2 rounded-lg border text-sm bg-transparent"
                           style={{ borderColor: 'var(--border)' }} />
                    <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
                            data-testid="action-agent-select"
                            className="px-3 py-2 rounded-lg border text-sm bg-transparent"
                            style={{ borderColor: 'var(--border)' }}>
                        <option value="">Assigner à…</option>
                        {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <input type="date" value={dueDate}
                           onChange={(e) => setDueDate(e.target.value)}
                           data-testid="action-due-input"
                           className="px-3 py-2 rounded-lg border text-sm bg-transparent"
                           style={{ borderColor: 'var(--border)' }} />
                    <button type="button" onClick={create} disabled={busy}
                            data-testid="action-submit-btn"
                            className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                            style={{ background: '#681793' }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                    </button>
                </div>
            )}

            <div className="rounded-xl border overflow-hidden"
                 style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                {(data?.actions || []).length === 0 ? (
                    <p className="p-4 text-sm" style={{ color: 'var(--muted-foreground)' }}
                       data-testid="actions-empty">
                        {filter === 'open'
                            ? 'Aucune action en cours — créez-en une depuis l\'ordre du jour ou le bouton ci-dessus.'
                            : 'Aucune action terminée.'}
                    </p>
                ) : (
                    <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
                        {data.actions.map((a) => (
                            <li key={a.id} className="flex items-center gap-3 px-4 py-2.5"
                                data-testid={`action-row-${a.id}`}>
                                <button type="button" onClick={() => toggle(a)}
                                        data-testid={`action-toggle-${a.id}`}
                                        aria-label={a.status === 'open' ? 'Marquer fait' : 'Rouvrir'}>
                                    {a.status === 'done'
                                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                        : <Circle className="w-5 h-5" style={{ color: 'var(--muted-foreground)' }} />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${a.status === 'done' ? 'line-through opacity-60' : ''}`}>
                                        {a.title}
                                    </p>
                                    <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                                        {a.agent_name || '—'}
                                        {a.due_date && (
                                            <span style={isOverdue(a) ? { color: '#dc2626', fontWeight: 600 } : {}}>
                                                {' · échéance '}{fdate(a.due_date)}
                                                {isOverdue(a) && ' (en retard)'}
                                            </span>
                                        )}
                                        {a.status === 'done' && a.done_at && ` · fait le ${fdate(a.done_at)}`}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
